import React, { useEffect, useState } from 'react'
import { Button, Form, Modal, Table } from 'react-bootstrap'

import CasesService from '../../services/CasesService'
import { ROLE_NAMES } from '../../utils/Constants'
import FAIcon from '../FAIcon'

const ManageCaseMembersModal = ({ show, onHide, caseNumber, currentMembers, allUsers, onUpdateMembers }) => {
  const [members, setMembers] = useState(currentMembers || [])
  const [selectedRole, setSelectedRole] = useState('')
  const [selectedUser, setSelectedUser] = useState('')

  // Sync members when prop changes
  useEffect(() => {
    const sorted = (currentMembers || []).slice().sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase()
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase()
      if (nameA !== nameB) return nameA.localeCompare(nameB)
      return 0
    })
    setMembers(sorted)
  }, [currentMembers, show])

  const handleRemove = async (userId, role) => {
    try {
      // Optimistic update
      const newMembers = members.filter(m => !(m.id === userId && m.roles.includes(role))) // eslint-disable-line no-unused-vars
      // Wait, backend supports removing specific role. Frontend model might differ.
      // Current members model from OverviewPage is distinct users with an array of roles.
      // But the Requirement says "A user may have more than 1 role on a case, in which case list them on multiple row"
      // So we should treat the grid as (User, Role) pairs.

      const memberRoleToRemove = { userId, role }
      await CasesService.removeMembers(caseNumber, [memberRoleToRemove])

      onUpdateMembers() // Trigger refresh in parent
    } catch (e) {
      console.error('Failed to remove member', e)
    }
  }

  const handleAdd = async () => {
    if (!selectedRole || !selectedUser) return

    try {
      const memberRoleToAdd = { userId: selectedUser, role: selectedRole }
      await CasesService.addMembers(caseNumber, [memberRoleToAdd])

      setSelectedUser('')
      // setSelectedRole('') // Keep role selected for convenience? User requirement says "refresh the list of users"

      onUpdateMembers()
    } catch (e) {
      console.error('Failed to add member', e)
    }
  }

  // Flatten the members for display (User + Role pairs)
  // Input members is likely [ {id, firstName, lastName, roles: [], role: 'foo' } ]
  // Wait, I updated the Controller to return { ...UserResponse, role: "foo" } for EACH row in DB.
  // And the controller SelectMany flattens them.
  // So the incoming `currentMembers` from OverviewPage.js (which uses `useEffect` to fetch)
  // currently does `setCaseMembers(caseObj.caseMembers)`.
  // The API returns an array of objects, e.g. [{id: 1, name: Jeff, role: 'case-manager'}, {id: 1, name: Jeff, role: 'claims-rep'}]
  // So `currentMembers` is ALREADY valid for the grid! We just iterate it.

  const availableUsers = allUsers.filter(u => {
    // Filter users who HAVE the selected global role?
    // Requirement: "populated with users who are assigned that role"
    return u.roles && u.roles.includes(selectedRole)
  })

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Manage Case Members</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Table striped bordered hover size="sm" className="mb-4">
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th style={{ width: '50px' }}></th>
            </tr>
          </thead>
          <tbody>
            {members.length === 0 ? (
              <tr>
                <td colSpan="3" className="text-center text-muted">
                  No members assigned
                </td>
              </tr>
            ) : (
              members.map((m, idx) => (
                <tr key={`${m.id}-${m.role}-${idx}`}>
                  <td>
                    {m.firstName} {m.lastName}
                  </td>
                  <td>
                    <Form.Control
                      as="select"
                      size="sm"
                      value={m.role}
                      onChange={async e => {
                        const newRole = e.target.value
                        const oldRole = m.role
                        if (newRole && newRole !== oldRole) {
                          setMembers(prev =>
                            prev.map(member => {
                              if (member.id === m.id && member.role === oldRole) {
                                return { ...member, role: newRole }
                              }
                              return member
                            }),
                          )
                          try {
                            // 1. Remove old member role
                            await CasesService.removeMembers(caseNumber, [{ userId: m.id, role: oldRole }])
                            // 2. Add new member role
                            await CasesService.addMembers(caseNumber, [{ userId: m.id, role: newRole }])
                            onUpdateMembers()
                          } catch (err) {
                            console.error('Failed to reassign role', err)
                            onUpdateMembers()
                          }
                        }
                      }}
                    >
                      {(() => {
                        const dbUser = allUsers.find(u => u.id === m.id)
                        const allowedRoles = dbUser?.roles || []
                        const isKnownRole = ROLE_NAMES.some(r => r.key === m.role)

                        const displayRoles = ROLE_NAMES.filter(r => allowedRoles.includes(r.key) || r.key === m.role)

                        return (
                          <>
                            {!isKnownRole && (
                              <option value={m.role} disabled>
                                {m.role === 'creator' ? 'Creator' : m.role}
                              </option>
                            )}
                            {displayRoles.map(r => (
                              <option key={r.key} value={r.key} disabled={!allowedRoles.includes(r.key)}>
                                {r.label}
                              </option>
                            ))}
                          </>
                        )
                      })()}
                    </Form.Control>
                  </td>
                  <td className="text-center">
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger p-0"
                      onClick={() => handleRemove(m.id, m.role)}
                    >
                      <FAIcon name="fa-solid fa-minus-circle" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        <div className="d-flex align-items-end">
          <Form.Group className="mb-0 mr-3 flex-grow-1">
            <Form.Label>Role</Form.Label>
            <Form.Control
              as="select"
              value={selectedRole}
              onChange={e => {
                setSelectedRole(e.target.value)
                setSelectedUser('')
              }}
            >
              <option value="">Select Role...</option>
              {ROLE_NAMES.map(r => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
          <Form.Group className="mb-0 mr-3 flex-grow-1">
            <Form.Label>User</Form.Label>
            <Form.Control
              as="select"
              value={selectedUser}
              onChange={e => setSelectedUser(e.target.value)}
              disabled={!selectedRole}
            >
              <option value="">{selectedRole ? 'Select User...' : 'Select a Role first'}</option>
              {availableUsers.map(u => (
                <option key={u.id} value={u.id}>
                  {u.firstName} {u.lastName}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
          <Button variant="primary" onClick={handleAdd} disabled={!selectedRole || !selectedUser} className="mb-0">
            <FAIcon name="fa-solid fa-plus" className="mr-1" /> Add
          </Button>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ManageCaseMembersModal
